<template>
    <page
        v-if="tabs.length"
        :busy="busy"
        :tabs="tabs"
        :can-add-tab="true"
        :can-remove-tab="false"
        :compact-mobile-view="true"
        :can-copy="true"
        @copy-url="copyToClipboard"
        @add-tab="addNewWebsite"
        class="i-wordpress-page"
    >
        <b-col v-if="currentWebsitePage">
            <b-row class="action-buttons mt-2 mb-2">
                <responsive-button
                    :icon="['fas', 'gears']"
                    variant="primary"
                    @click="openSettingsModal"
                    class="mr-2 mb-1 ml-1"
                    :disabled="!userAllowWordpress"
                >
                    Settings
                </responsive-button>
                <responsive-button
                    :icon="['fas', 'user-check']"
                    variant="primary"
                    @click="verifyWebsite"
                    class="mr-2 mb-1"
                    :disabled="!userAllowWordpress"
                >
                    Verify
                </responsive-button>
                <responsive-button
                    :icon="['fas', 'rotate']"
                    variant="primary"
                    @click="regenerateWebsite"
                    class="mr-2 mb-1"
                    :disabled="!userAllowWordpress"
                >
                    Regenerate
                </responsive-button>
                <responsive-button
                    :icon="['fas', 'download']"
                    variant="primary"
                    class="mr-2 mb-1"
                    @click="fetchWebsiteUrls"
                    :disabled="!userAllowWordpress"
                >
                    Fetch Property URLs
                </responsive-button>
                <responsive-button
                    v-if="tabs.length > 1"
                    class="mb-1"
                    :icon="['fas', 'trash']"
                    variant="primary"
                    @click="handleRemoveWebsite"
                    :disabled="!userAllowWordpress"
                >
                    Delete
                </responsive-button>
            </b-row>
        </b-col>
        <page
            :busy="busy"
            :tabs="subTabs"
            :can-add-tab="false"
            :can-edit-tab-title="false"
            :can-remove-tab="false"
            v-if="currentWebsitePage"
            class="i-wordpress-subtabs-page"
        >
            <website-property-category-lists
                :websiteId="websiteId"
                :isPropertiesTab="isPropertiesTab"
                :isCategoriesTab="isCategoriesTab"
                :subTabsDataIsLoaded="subTabsDataIsLoaded"
                :onSwitchWebsite="onSwitchWebsite"
                :websiteUrl="websiteLink"
            />
        </page>
        <modal
            v-if="currentWebsitePage"
            :id="removeWebsitePageModalId"
            title="Delete website"
            @ok="deleteCurrentWebsitePage"
        >
            Website{{ currentWebsitePage.url ? ` "${currentWebsitePage.url}" ` : ' ' }}will be deleted from Lodgix. Are
            you sure?
        </modal>
        <validation-observer v-slot="{ meta, errors, handleSubmit, setValues }">
            <modal
                :id="settingsWebsitePageModalId"
                title="Website Settings"
                :ok-disabled="!meta.valid"
                @ok.prevent="handleSubmit(_, updateWebsite)"
                @hidden="resetValues"
                @shown="
                    setValues({
                        name,
                        url,
                    })
                "
            >
                <b-row v-if="currentWebsitePage" class="flex-column-reverse flex-lg-row flex-md-row">
                    <b-col class="pr-0">
                        <div class="mr-4 mb-1 d-flex justify-content-between align-items-center">
                            <div class="d-flex flex-md-row flex-lg-row">
                                Customer Id:&nbsp;
                                <strong> {{ currentWebsitePage.owner_id }}-{{ currentWebsitePage.sort_order }} </strong>
                            </div>
                        </div>
                        <div class="d-flex flex-column flex-md-column flex-lg-row">
                            <div v-if="currentWebsitePage.version" class="d-flex flex-md-row flex-lg-row">
                                Version:&nbsp;
                                <strong class="mr-2 mb-lg-2 mb-1">
                                    {{ currentWebsitePage.version }}
                                </strong>
                            </div>
                            <div v-if="currentWebsitePage.plugin_version" class="d-flex flex-md-row flex-lg-row">
                                Plugin Version:&nbsp;
                                <strong>
                                    {{ currentWebsitePage.plugin_version }}
                                </strong>
                            </div>
                        </div>
                        <div class="mr-4 mt-lg-0 mt-md-0 mt-1 d-flex flex-md-row flex-lg-row">
                            <span class="text-nowrap">API Key:&nbsp;</span>
                            <strong
                                class="d-flex no-wrap"
                                @click="copyToClipboard(currentWebsitePage.limited_api_key_2)"
                            >
                                <font-awesome-icon icon="copy" class="copy-button ml-1" />
                            </strong>
                        </div>
                    </b-col>
                    <b-col class="d-flex justify-content-lg-end justify-content-md-end">
                        <div class="enable-website mt-1 mb-2">
                            <form-switch v-model="is_enabled" />
                            <label>Enabled</label>
                        </div>
                    </b-col>
                </b-row>
                <div class="pl-0 mt-3">
                    <div class="d-flex flex-column flex-lg-row mb-3">
                        <div class="name pr-4">
                            <validation-provider v-slot="{ handleChange }" name="name" rules="required">
                                <form-input
                                    floating-label="Name"
                                    v-model="name"
                                    @blur="onNameChange"
                                    :error="errors.first_name"
                                    @input="handleChange"
                                ></form-input>
                            </validation-provider>
                        </div>
                    </div>
                    <div class="d-flex flex-column flex-lg-row">
                        <div>
                            <div class="d-flex flex-column flex-lg-row">
                                <div class="mr-1 select-prefix">
                                    <form-select
                                        floating-label="Protocol"
                                        v-model="selectedUrlPrefix"
                                        :options="selectOptions"
                                    >
                                    </form-select>
                                </div>
                                <div class="url mr-1">
                                    <validation-provider v-slot="{ handleChange }" name="url" rules="required">
                                        <form-input
                                            floating-label="Url"
                                            :value="url"
                                            :error="errors.url"
                                            ref="urlRef"
                                            @input="
                                                (val) => {
                                                    onInputUrlChange(val);
                                                    handleChange(val);
                                                }
                                            "
                                        ></form-input>
                                    </validation-provider>
                                </div>
                                <div class="permalink">
                                    <form-input floating-label="Permalink" v-model="permalink"></form-input>
                                </div>
                            </div>
                        </div>
                    </div>
                    <b-button
                        class="mb-2 mt-3 collapse-button"
                        variant="default"
                        @click="visibleScripts = !visibleScripts"
                    >
                        <span>Tracking Scripts</span>
                        <font-awesome-icon :icon="['fas', visibleScripts ? 'arrow-up' : 'arrow-down']" />
                    </b-button>
                    <b-collapse id="collapse-scripts" v-model="visibleScripts" class="pt-2">
                        <tracking-script
                            :label="
                                'Insert tracking scripts here. Scripts added here will be served on all ' +
                                'Lodgix booking pages when bookings are made via this WordPress site. ' +
                                'You can use these scripts to track booking conversions. Keep in mind, ' +
                                'these scripts will overwrite those on the Important Settings page, ' +
                                'but only for bookings from this site.'
                            "
                            :tracking-scripts="trackingScripts"
                            :tracking-header="trackingHeader"
                            :ga-tracking-id="gaTrackingId"
                            @input-tracking-scripts="onInputTrackingScripts"
                            @change-tracking-header="onChangeTrackingHeader"
                            @setup-cross-domain-tracking="setupCrossDomainTracking"
                            @disable-cross-domain-tracking="disableCrossDomainTracking"
                        />
                    </b-collapse>
                    <div class="mt-3" v-if="currentWebsitePage.url">
                        <a :href="websiteLink" target="_blank"> Visit Site </a>
                    </div>
                </div>
            </modal>
        </validation-observer>
        <modal
            v-if="currentWebsitePage"
            :id="fetchUrlsModalId"
            title="Fetch property URLs"
            @ok="fetchUrlsOfCurrentWebsite"
        >
            This will replace all existing property URLs. Are you sure?
        </modal>
    </page>
</template>

<script>
import { mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import Page from '../../generic/page/page.vue';
import Modal from '../../generic/modal/modal.vue';
import FormSelect from '../../generic/form-select/form-select.vue';
import FormSwitch from '../../generic/form-switch/form-switch.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import WebsitePropertyCategoryLists from './website-property-category-lists.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import TrackingScript from '../../common/tracking-script.vue';
import API from '../../../api';
import {
    WORDPRESS_SETTINGS_WEBSITE_FETCH_ENDPOINT,
    WORDPRESS_SETTINGS_WEBSITE_REGENERATE_ENDPOINT,
    WORDPRESS_SETTINGS_WEBSITE_VERIFY_ENDPOINT,
    WORDPRESS_SETTINGS_WEBSITE_GENERATE_GA_TRACKING_SCRIPT_ENDPOINT,
} from '../../../endpoints';
import { formatResponseError } from '../../../utils/api';
import { removeScrollMixin } from '../../../mixins/remove-scroll-mixin';

export default {
    components: {
        Page,
        Modal,
        FormSwitch,
        ResponsiveButton,
        WebsitePropertyCategoryLists,
        FormInput,
        FormSelect,
        TrackingScript,
    },
    mixins: [removeScrollMixin],
    computed: {
        ...mapGetters('header', ['userAllowWordpress']),
        ...mapGetters('wordpressSettings', [
            'websites',
            'currentWebsitePage',
            'websitePageByID',
            'currentWebsitesProperties',
            'currentWebsitesCategories',
        ]),
        websiteLink() {
            const { permalink } = this.currentWebsitePage;
            return `${this.selectedUrlPrefix + this.url}/${permalink}/`;
        },
        currentRouteID() {
            return parseInt(this.$route.query.id);
        },
        tabs() {
            return this.websites
                ? this.websites.map((website) => ({
                      id: website.id,
                      title: website.name,
                      route: this.$route.path + `?id=${website.id}`,
                      active: !!this.currentWebsitePage && this.currentWebsitePage.id === website.id,
                      currentUrl: website.url || "URL doesn't exists",
                  }))
                : [];
        },
        isPropertiesTab() {
            return this.currentSubTabId === this.TAB_ID_PROPERTIES;
        },
        isCategoriesTab() {
            return this.currentSubTabId === this.TAB_ID_CATEGORIES;
        },
        websiteId() {
            return this.currentWebsitePage && this.currentWebsitePage.id;
        },
    },
    data() {
        return {
            busy: false,
            removeWebsitePageModalId: 'remove-website-page-modal',
            settingsWebsitePageModalId: 'settings-website-page-modal',
            fetchUrlsModalId: 'fetch-urls-website-modal',
            TAB_ID_PROPERTIES: 'properties',
            TAB_ID_CATEGORIES: 'categories',
            currentSubTabId: null,
            subTabs: [],
            url: null,
            name: null,
            permalink: null,
            is_enabled: null,
            gaTrackingId: '',
            trackingScripts: '',
            trackingHeader: false,
            visibleScripts: false,
            onSwitchWebsite: null,
            selectedUrlPrefix: null,
            isDisableOkButton: false,
            selectOptions: [
                { value: 'http://', text: 'http://' },
                { value: 'https://', text: 'https://' },
            ],
        };
    },
    mounted() {
        this.fetchWordpressWebsites();
        this.subTabs = [
            { id: this.TAB_ID_PROPERTIES, title: 'Properties', active: true, callback: this.handleTabChange },
            { id: this.TAB_ID_CATEGORIES, title: 'Categories', active: false, callback: this.handleTabChange },
        ];
        this.currentSubTabId = this.TAB_ID_PROPERTIES;
    },
    unmounted() {
        this.$store.dispatch('wordpressSettings/resetWebsitesData');
    },
    methods: {
        async copyToClipboard(value) {
            try {
                await navigator.clipboard.writeText(value);
                this.successToast({ text: 'Copied to clipboard successfully.' });
            } catch (err) {
                this.errorToast({ text: 'Something went wrong while copying to clipboard.' });
            }
        },
        async fetchWordpressWebsites() {
            this.busy = true;
            try {
                await this.$store.dispatch('wordpressSettings/fetchWordpressWebsites');
            } catch (err) {
                this.errorToast({ text: 'Could not load wordpress settings page data.' });
            }
            this.busy = false;

            if (this.currentRouteID) {
                this.$store.commit('wordpressSettings/assignCurrentWebsitePage', { id: this.currentRouteID });
            } else {
                this.$store.commit('wordpressSettings/assignFirstCurrentWebsitePage');
            }
        },
        async addNewWebsite() {
            if (!this.userAllowWordpress) {
                return;
            }
            this.busy = true;
            try {
                await this.$store.dispatch('wordpressSettings/addWordpressWebsite');
                const newWebsite = this.websites.find((i, index) => index === this.websites.length - 1);
                this.$store.commit('wordpressSettings/assignCurrentWebsitePage', {
                    id: newWebsite ? newWebsite.id : this.websites[0].id,
                });
            } catch (err) {
                this.errorToast({ text: 'Could not add new website.' });
            }
            this.busy = false;
        },
        async removeWebsite(id) {
            this.busy = true;
            try {
                await this.$store.dispatch('wordpressSettings/removeWordpressWebsite', { id });
            } catch (err) {
                this.errorToast({ text: 'Could not remove website.' });
            }
            this.busy = false;
        },
        deleteCurrentWebsitePage() {
            this.removeWebsite(this.currentWebsitePage.id);
        },
        resetValues() {
            this.url = this.currentWebsitePage.url.replace(/https?:\/\//gi, '');
            this.name = this.currentWebsitePage.name;
            this.permalink = this.currentWebsitePage.permalink;
            this.is_enabled = this.currentWebsitePage.is_enabled;
            this.gaTrackingId = this.currentWebsitePage.ga_tracking_id;
            this.trackingScripts = this.currentWebsitePage.tracking_scripts;
            this.trackingHeader = this.currentWebsitePage.tracking_header;
            this.visibleScripts = false;
        },
        parseUrl(value) {
            const isHttp = new RegExp('^http://', 'g').test(value);
            const isHttps = new RegExp('^https://', 'g').test(value);
            return isHttp ? 'http://' : isHttps ? 'https://' : 'https://';
        },
        onInputUrlChange(value) {
            this.selectedUrlPrefix = this.parseUrl(value);
            this.url = value.replace(/^https?\:\/\//i, '');
        },
        openSettingsModal() {
            this.selectedUrlPrefix = this.parseUrl(this.currentWebsitePage.url);
            this.url = this.currentWebsitePage.url.replace(/https?:\/\//gi, '');
            this.$bvModal.show(this.settingsWebsitePageModalId);
        },
        async fetchUrlsOfCurrentWebsite() {
            this.busy = true;

            try {
                await API.get(WORDPRESS_SETTINGS_WEBSITE_FETCH_ENDPOINT(this.currentWebsitePage.id));
                this.successToast({
                    text: `Property URLs successfully fetched from ${this.currentWebsitePage.name}.`,
                });
            } catch (err) {
                this.errorToast({ text: `Could not fetch property URLs from ${this.currentWebsitePage.name}.` });
            }
            this.busy = false;
        },
        async updateWebsite() {
            this.busy = true;
            const nameIsValid = this.onNameChange();

            if (!nameIsValid) {
                this.onNameChange(this.name);
            }
            try {
                await this.$store.dispatch('wordpressSettings/updateWebsite', {
                    id: this.currentWebsitePage.id,
                    data: {
                        name: this.name,
                        permalink: this.permalink,
                        url: this.selectedUrlPrefix + this.url,
                        is_enabled: this.is_enabled,
                        ga_tracking_id: this.gaTrackingId,
                        tracking_scripts: this.trackingScripts,
                        tracking_header: this.trackingHeader,
                    },
                });
                this.$nextTick(() => {
                    this.$bvModal.hide(this.settingsWebsitePageModalId);
                });
                this.successToast({ text: `Website settings successfully updated.` });
            } catch (err) {
                const { generic, fields } = formatResponseError(err);
                this[name] = this.currentWebsitePage[name];

                if (generic) {
                    this.errorToast({ text: generic });
                }

                if (!isEmpty(fields)) {
                    this.$refs.form.setErrors(fields);
                }
            } finally {
                this.busy = false;
            }
        },
        async verifyWebsite() {
            this.busy = true;
            try {
                const response = await API.get(WORDPRESS_SETTINGS_WEBSITE_VERIFY_ENDPOINT(this.currentWebsitePage.id));
                this.currentWebsitePage.version = response.data.version;
                this.currentWebsitePage.plugin_version = response.data.plugin_version;
                this.successToast({ text: `${this.currentWebsitePage.name} has been verified.` });
            } catch (err) {
                this.errorToast({ text: `Could not verify ${this.currentWebsitePage.name}.` });
            }

            this.busy = false;
        },
        async regenerateWebsite() {
            this.busy = true;

            try {
                const response = await API.get(
                    WORDPRESS_SETTINGS_WEBSITE_REGENERATE_ENDPOINT(this.currentWebsitePage.id)
                );
                this.currentWebsitePage.version = response.data.version;
                this.currentWebsitePage.plugin_version = response.data.plugin_version;
                this.successToast({ text: `${this.currentWebsitePage.name} has been regenerated.` });
            } catch (err) {
                this.errorToast({ text: `Could not regenerate ${this.currentWebsitePage.name}.` });
            }

            this.busy = false;
        },
        handleRemoveWebsite(tab) {
            this.$bvModal.show(this.removeWebsitePageModalId);
        },
        fetchWebsiteUrls() {
            this.$bvModal.show(this.fetchUrlsModalId);
        },
        handleTabChange(tabTile, index) {
            const newTabs = this.subTabs.map((i, iIndex) => ({ ...i, active: index === iIndex }));
            this.subTabs = newTabs;
            this.currentSubTabId = newTabs[index].id;
        },
        onUrlChange(event) {
            this.isDisableOkButton = !event.target.value || !!this.$refs?.urlRef?.error || false;
        },
        onNameChange(value) {
            if (!value) {
                return false;
            }

            const sameNameWebsite = this.websites.find(
                (i) => +i.id !== +this.currentWebsitePage.id && i.name === value
            );

            if (sameNameWebsite) {
                this.errorToast({ text: `Website name must be unique.` });
                this.name = this.currentWebsitePage.name;

                return false;
            }
        },
        subTabsDataIsLoaded() {
            this.onSwitchWebsite = false;
        },
        onInputTrackingScripts(value) {
            this.trackingScripts = value;
        },
        onChangeTrackingHeader(value) {
            this.trackingHeader = value;
        },
        async setupCrossDomainTracking(gaTrackingId) {
            this.busy = true;
            try {
                const { data } = await API.post(WORDPRESS_SETTINGS_WEBSITE_GENERATE_GA_TRACKING_SCRIPT_ENDPOINT(), {
                    ga_tracking_id: gaTrackingId,
                });
                this.trackingScripts = data.tracking_scripts;
                this.gaTrackingId = gaTrackingId;
                this.trackingHeader = true;
            } catch (e) {
                this.errorToast({ text: 'Could not generate GA Cross Domain Tracking.' });
            } finally {
                this.busy = false;
            }
        },
        disableCrossDomainTracking() {
            this.trackingScripts = '';
            this.gaTrackingId = '';
        },
    },
    watch: {
        currentRouteID(id) {
            this.$store.commit('wordpressSettings/assignCurrentWebsitePage', { id });
            this.onSwitchWebsite = true;
            this.handleTabChange('', 0);
        },
        currentWebsitePage() {
            this.$router
                .push({
                    path: this.$route.path,
                    query: { id: this.currentWebsitePage.id },
                })
                .catch((err) => {});
            this.url = this.currentWebsitePage.url.replace(/https?:\/\//gi, '');
            this.name = this.currentWebsitePage.name;
            this.permalink = this.currentWebsitePage.permalink;
            this.is_enabled = this.currentWebsitePage.is_enabled;
            this.gaTrackingId = this.currentWebsitePage.ga_tracking_id;
            this.trackingScripts = this.currentWebsitePage.tracking_scripts;
            this.trackingHeader = this.currentWebsitePage.tracking_header;
        },
    },
};
</script>

<style lang="scss" scoped>
@import '~bootstrap/scss/_functions.scss';
@import '~bootstrap/scss/_variables.scss';
@import '~bootstrap/scss/mixins/_breakpoints.scss';

fieldset.form-group {
    margin-bottom: 0;
}

.copy-button {
    cursor: pointer;
    font-weight: normal;
    color: #6c757d;
    font-size: 16px;
    transition: 0.3s color;
    &:hover {
        color: #cdcdcd;
    }
}

.enable-website {
    label + label {
        margin-bottom: 0;
    }
}

.url,
.permalink {
    display: flex;

    input {
        margin-right: 15px;
        width: 90%;
    }
}

.select-prefix {
    width: 30%;
}

@include media-breakpoint-down(lg) {
    .action-buttons {
        margin-top: 16px;
    }
    .select-prefix {
        width: 30%;
    }
}
@include media-breakpoint-down(md) {
    .permalink {
        margin-top: 16px;
    }
    .url {
        margin-top: 16px;
    }
    .select-prefix {
        width: 50%;
    }
}

@include media-breakpoint-down(sm) {
    .permalink {
        margin-top: 16px;
    }
    .url {
        margin-top: 16px;
    }
    .select-prefix {
        width: 70%;
    }
}

.collapse-button {
    display: flex !important;
    width: 182px;
    justify-content: space-between;
    align-items: center;
}

@include media-breakpoint-down(md) {
    .collapse-button {
        width: 207px;
    }
}
</style>
